import { PermissionViewModel } from "../ViewModels";
import {
    ActionResponse,
    ActionStatusHelper,
    DOMAIN_CONFIGS,
    DomainDetailHook,
    DomainHookProps,
    useAppEnum,
    useAuditEnricher,
    useDetailLifeCycle,
} from "@/Shared";
import { PermissionModel } from "../Models";


/**
 * usePermissionDetail hook
 *
 * Domain-specific detail hook for Permission entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the PermissionViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, delete handler, access rights, and navigation methods to the UI layer.
 * 
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<PermissionViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<PermissionViewModel>
 */
export function usePermissionDetail(props: DomainHookProps): DomainDetailHook<PermissionViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Permission;

    /**
     * Permission-related enums exposed to the UI layer
     */
    const { PermissionEnums } = useAppEnum();

    /**
     * Enrichment layer
     *
     * Responsible for resolving user-related data and
     * computing derived Permission fields.
     */
    const { enricherResponse, getEnrichedAudit, } = useAuditEnricher<PermissionModel>();

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: permissionHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: permissionHook,
        buildVM: (currentEntity: PermissionModel, isDisplaying: boolean) => {
            return new PermissionViewModel({
                permission: getEnrichedAudit(currentEntity),
                viewEnums: PermissionEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}